import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthContextProvider, useAuth } from './context/AuthContext';
import { AgentProvider, useAgent } from './context/AgentContext';
import { ALLOWED_GROUPS } from './components/AccessGate/AccessGate';
import { hasIvtAccess } from './ivt/utils/authorization';
import { hasEventHubAccess } from './eventHub/utils/authorization';
import { ImpersonationProvider } from './context/ImpersonationContext';
import { UserProfileProvider } from './context/UserProfileContext';
import { WorkspaceNavProvider, useWorkspaceNav } from './context/WorkspaceNavContext';
import { ChatPanelProvider, useChatPanel } from './context/ChatPanelContext';
import { ReportMetadataProvider } from './context/ReportMetadataContext';
import { useEmailDeepLink } from './hooks/useEmailDeepLink';
import { fullWidthRouteOf, useCollapseChatOnArrival } from './hooks/useCollapseChatOnArrival';
import { isCompact, useBreakpoint } from './hooks/useBreakpoint';
import { useVisualViewportVars } from './hooks/useVisualViewportVars';
import AccessGate from './components/AccessGate/AccessGate';
import Sidebar from './components/Sidebar/Sidebar';
import MobileTopBar from './components/MobileTopBar/MobileTopBar';
import ChatPanel from './components/ChatPanel/ChatPanel';
import ImpersonationBanner from './components/ImpersonationBanner/ImpersonationBanner';
import WorkspacePage from './pages/WorkspacePage/WorkspacePage';
import SkillMarketplace from './pages/SkillMarketplace/SkillMarketplace';
import TaskPage from './pages/TaskPage/TaskPage';
import CheetahPage from './pages/CheetahPage/CheetahPage';
import ApiTestPage from './pages/ApiTestPage';
import IvtLayout from './ivt/components/IvtLayout/IvtLayout';
import IvtLanding from './ivt/components/IvtLanding/IvtLanding';
import SolutionMapPage from './ivt/pages/SolutionMapPage/SolutionMapPage';
import AssetsOverviewPage from './ivt/pages/AssetsPage/AssetsOverviewPage';
import AssetDetailPage from './ivt/pages/AssetsPage/AssetDetailPage';
import StorylineDetailPage from './ivt/pages/StorylineDetailPage/StorylineDetailPage';
import UseCaseDetailPage from './ivt/pages/AssetsPage/UseCaseDetailPage';
import DashboardPage from './ivt/pages/DashboardPage/DashboardPage';
import MotionsPage from './ivt/pages/MotionsPage/MotionsPage';
import WaveCreationPage from './ivt/pages/WaveCreationPage/WaveCreationPage';
import MotionDetailPage from './ivt/pages/MotionDetailPage/MotionDetailPage';
import AccountSettingsPage from './pages/AccountSettingsPage/AccountSettingsPage';
import AboutResponsesPage from './pages/AboutResponsesPage/AboutResponsesPage';
import NewUseCasesPage from './ivt/pages/UseCasesPage/NewUseCasesPage';
import ConfigurationPage from './ivt/pages/ConfigurationPage/ConfigurationPage';
import RadarConfigPage from './ivt/pages/RadarConfigPage/RadarConfigPage';
import EventHubLayout from './eventHub/components/EventHubLayout/EventHubLayout';
import EventHubHomePage from './eventHub/pages/EventHubHomePage/EventHubHomePage';
import LifecycleDataPage from './eventHub/pages/LifecycleDataPage/LifecycleDataPage';
import EventDetailPage from './eventHub/pages/EventDetailPage/EventDetailPage';
import EditEventPage from './eventHub/pages/EditEventPage/EditEventPage';
import CreateNominationPage from './eventHub/pages/CreateNominationPage/CreateNominationPage';
import EditNominationPage from './eventHub/pages/EditNominationPage/EditNominationPage';
import NominationListPage from './eventHub/pages/NominationListPage/NominationListPage';
import { AG_UI_ENABLED } from './config';
import { chatAgentFor } from './utils/chatAgent';
import './App.css';
import AgentPlatformLogin from './pages/AgentPlatformLogin/AgentPlatformLogin';
import CloudscapeI18nBoundary from './i18n/CloudscapeI18nBoundary';
import EvalLayout from './eval/components/EvalLayout/EvalLayout';
import EvalPlaceholderPage from './eval/pages/EvalPlaceholderPage/EvalPlaceholderPage';
import EvalHomePage from './eval/pages/EvalHomePage/EvalHomePage';
import EvalEvaluatorsPage from "./eval/pages/EvalEvaluatorsPage/EvalEvaluatorsPage";
// @ts-ignore deprecated page kept for reference
import GoldenDatasetCrudPage from './eval/pages/GoldenDatasetCrudPage/GoldenDatasetCrudPage';
import GoldenDatasetsV2Page from "./eval/pages/GoldenDatasetsV2Page/GoldenDatasetsV2Page";
import GoldenCandidatesPage from "./eval/pages/GoldenCandidatesPage/GoldenCandidatesPage";
import EvalReportsPage from './eval/pages/EvalReportsPage/EvalReportsPage';
import UsageAnalyticsPage from './eval/pages/UsageAnalyticsPage/UsageAnalyticsPage';
import InsightsPage from './eval/pages/InsightsPage/InsightsPage';
import TasksPage from './eval/pages/TasksPage/TasksPage';
import EvalLandingPage from "./eval/pages/EvalLandingPage/EvalLandingPage";
import CpaLayout from './cpa/components/CpaLayout/CpaLayout';
import AccessControlPage from './pages/AccessControlPage/AccessControlPage';
import AdminRbmsPage from './pages/AdminRbmsPage/AdminRbmsPage';
import EditWorkspaceHomepage from './pages/EditWorkspaceHomepage/EditWorkspaceHomepage';

/**
 * ChatShell — persistent shell that keeps `<ChatPanel />` mounted as a single
 * fiber across route changes between IVT (/ivt/*) and the main app (/*).
 *
 * Because ChatPanel lives HERE (not inside IvtLayout or MainAppContent), its
 * internal state — sessionId, messages, streaming, pendingFiles, scope
 * bindings — survives when the user navigates between the two surfaces.
 * Prior to this shell, each surface mounted its own <ChatPanel />, so
 * cross-surface navigation dropped the whole chat session.
 *
 * Route content renders through <Outlet />; whichever child route matched
 * lives to the left of the persistent ChatPanel column.
 */
function ChatShell() {
  const { chatCollapsed, toggleChat, chatFullscreen, exitFullscreen } = useChatPanel();
  const { ldap_groups } = useAuth();
  const location = useLocation();
  const isIvt = location.pathname === '/ivt' || location.pathname.startsWith('/ivt/');
  const isEventHub = location.pathname === '/event' || location.pathname.startsWith('/event/');
  // Independent product surfaces (IVT, Event Hub) share the ChatShell but each
  // has its own access gate and no email deep-link. Only IVT also has no
  // fullscreen; Event Hub supports it as the main app does.
  const isModuleSurface = isIvt || isEventHub;

  // Per-surface chat authorization. ChatPanel lives outside <AccessGate> in
  // this shell, so we can't rely on the gate to hide it — we compute the same
  // permission ourselves. Keeps parity with the pre-hoist behaviour where
  // ChatPanel only rendered for the main-app groups (ALLOWED_GROUPS) on the main app and for
  // any IVT group on /ivt/*. Users who fail the current surface's gate get NO chat
  // (and no deep-link auto-send) alongside the denial screen.
  // MUST stay in step with IvtLayout's own gate: this is a SECOND enforcement
  // point, so a mismatch shows an admitted user the IVT pages with no chat panel.
  const surfaceAllowsChat = isIvt
    ? hasIvtAccess(ldap_groups)
    : isEventHub
      ? hasEventHubAccess(ldap_groups)
      : ldap_groups.some((g) => ALLOWED_GROUPS.includes(g));

  // Email deep-links (?prompt=…&autoSend=1, plus the pre-Federate sessionStorage
  // stash) are authored for the main-app Rocky agent. Firing them into IVT chat
  // — where the agent + KB scope are different — would silently misroute the
  // prompt for an IVT Hub user whose first post-login landing is /ivt. Gate to
  // main-app surfaces AND passing the same surface-allows-chat check the panel
  // itself uses.
  useEmailDeepLink(!isModuleSurface && surfaceAllowsChat);

  // IVT doesn't support fullscreen chat (it hides the toggle). If the user
  // enters IVT while fullscreen was left on from the main app, drop it.
  useEffect(() => {
    if (isIvt && chatFullscreen) exitFullscreen();
  }, [isIvt, chatFullscreen, exitFullscreen]);

  // Full-width surfaces (see FULL_WIDTH_ROUTES) start with chat collapsed. On arrival
  // only — the user can still open it and it stays open while they are there. The matched
  // route is passed, not a boolean, so moving between two full-width surfaces counts as a
  // new arrival; this shell does not remount per route.
  useCollapseChatOnArrival(fullWidthRouteOf(location.pathname));

  // Responsive layout — main app ONLY. IVT and Event Hub stay desktop-only (their
  // owners asked for them to be left alone), so they never get the responsive class
  // and every small-screen rule in App.css is scoped under it.
  // Below desktop the chat stops being a column and slides over the content instead
  // (App.css), so arriving there with it open would cover the page the user came to
  // see: collapse it on entering compact — once, keyed on `compact`, the same
  // arrival-not-held rule as useCollapseChatOnArrival, so the user can still open it.
  // Fullscreen is meaningless when the overlay already fills the screen, so drop it.
  const breakpoint = useBreakpoint();
  const compact = !isModuleSurface && isCompact(breakpoint);
  useEffect(() => {
    if (!compact) return;
    if (!chatCollapsed) toggleChat();
    if (chatFullscreen) exitFullscreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compact]);
  // While that overlay is open, keep it on the visible part of the screen when the
  // on-screen keyboard shrinks and pans the viewport (App.css reads the variables).
  useVisualViewportVars(compact && surfaceAllowsChat && !chatCollapsed);

  // Agent selection for the shared ChatPanel:
  //  - The main app, IVT and Event Hub all use the user's agent (AgentContext:
  //    their manual choice, else the job-role default among the agents they may
  //    invoke), so the agentName prop stays stable when navigating between them
  //    — avoiding a spurious ChatPanel reset (ChatShell is a single persistent
  //    fiber). On the main app, dev/beta still honor ?agent=... for AG-UI —
  //    except for an agent the user may not select (AgentContext.isAgentAllowedFor),
  //    which falls back to theirs.
  const { currentAgent, isAgentAllowed } = useAgent();
  const requestedAgent = chatAgentFor(currentAgent, {
    isModuleSurface,
    agUiEnabled: AG_UI_ENABLED,
    search: window.location.search,
  });
  const agentOverride = isAgentAllowed(requestedAgent) ? requestedAgent : currentAgent;

  // In fullscreen mode, hide the content column so ChatPanel takes the full
  // viewport. Not applied on IVT, which has no fullscreen.
  const hideContent = chatFullscreen && !chatCollapsed && !isIvt && surfaceAllowsChat;

  return (
    // Module surfaces keep their inline 100vh untouched; the main app's height comes
    // from .app-shell--responsive, which adds 100dvh for mobile browser toolbars.
    <div
      className={isModuleSurface ? undefined : 'app-shell--responsive'}
      style={isModuleSurface ? { display: 'flex', height: '100vh' } : undefined}
    >
      <div
        className="app-shell__content"
        style={hideContent
          ? { display: 'none' }
          : { flex: 1, minWidth: 0, display: 'flex', overflow: 'hidden' }}
      >
        <Outlet />
      </div>
      {/* Tap-outside-to-close for the compact chat overlay. */}
      {compact && surfaceAllowsChat && !chatCollapsed && (
        <div className="app-shell__chat-backdrop" onClick={toggleChat} aria-hidden="true" />
      )}
      {surfaceAllowsChat && (
        <CloudscapeI18nBoundary>
          <ChatPanel
            isCollapsed={chatCollapsed}
            onToggle={toggleChat}
            agentName={agentOverride}
            hideFullscreen={isIvt || compact}
          />
        </CloudscapeI18nBoundary>
      )}
    </div>
  );
}

/**
 * MainAppContent — the main-app content column (Sidebar + routed pages).
 * NOTE: no longer owns <ChatPanel /> — that's hoisted to <ChatShell />
 * above, which is why chat sessions survive navigating out to /ivt/*.
 */
function NotFound() {
  const { t } = useTranslation('shell');
  return <div style={{ padding: '20px 32px' }}>{t('notFound')}</div>;
}

function MainAppContent() {
  // Phones only: the Sidebar drawer that MobileTopBar opens. Sidebar closes it on
  // its own clicks; this closes it on every other navigation (e.g. UserPopover's
  // links), so a new page never opens underneath it.
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setNavOpen(false), [pathname]);
  const { toggleNav } = useWorkspaceNav();

  return (
    <>
      <Sidebar drawer={{ open: navOpen, onClose: () => setNavOpen(false) }} />
      <main className="app-main">
        <MobileTopBar
          onOpenNav={() => setNavOpen(true)}
          onToggleFiles={pathname === '/' ? toggleNav : undefined}
        />
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<WorkspacePage />} />
          <Route path="/skills" element={<SkillMarketplace />} />
          <Route path="/tasks" element={<TaskPage />} />
          <Route path="/cheetah" element={<CheetahPage />} />
          <Route path="/test_api" element={<div style={{ padding: '20px 32px' }}><ApiTestPage /></div>} />
          <Route path="/admin/access-control" element={<AccessControlPage />} />
          <Route path="/admin-rbms" element={<AdminRbmsPage />} />
          <Route path="/admin-rbms/edit-homepage" element={<EditWorkspaceHomepage />} />
          {/* CPA (Credit Program Advisor) rule management. A CHILD of the backend
              management system, per the design — breadcrumb Rocky > Rocky Backend
              Management System > CPA, reusing the global icon rail rather than
              growing its own nav. It therefore inherits the main <AccessGate>
              (rocky-admin et al), which IS the right gate here: unlike /ivt and
              /eval, this page's audience IS the main-app admin audience.
              `/*` because CpaLayout routes its own five tabs.
              Stage gating lives on the ENTRY CARD in AdminRbmsPage (beta only), not
              on this route: the page is unfinished, not secret. */}
          <Route path="/admin-rbms/cpa/*" element={<CpaLayout />} />
          <Route path="/account-settings" element={<AccountSettingsPage />} />
          <Route path="/help/ai-responses" element={<AboutResponsesPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();

  if (pathname === '/login') {
    return (
      <CloudscapeI18nBoundary>
        <AgentPlatformLogin />
      </CloudscapeI18nBoundary>
    );
  }

  return (
    <Routes>
      <Route
        path="/eval/landing"
        element={
          <CloudscapeI18nBoundary>
            <EvalLandingPage />
          </CloudscapeI18nBoundary>
        }
      />
      {/* Evaluation — independent layout, outside ChatShell. EvalLayout mounts
          its OWN ChatPanel pinned to the eval agent (EVAL_CHAT_ENABLED).
          Access control is its OWN gate: EvalLayout enforces EVAL_PAGE_LDAP_GROUP
          internally. This route intentionally sits OUTSIDE <AccessGate> — do NOT
          wrap it (see the main-app route note below for why). */}
      <Route
        path="/eval"
        element={
          <CloudscapeI18nBoundary>
            <EvalLayout />
          </CloudscapeI18nBoundary>
        }
      >
        <Route index element={<EvalHomePage />} />
        <Route path="metrics" element={<EvalPlaceholderPage page="metrics" />} />
        <Route path="usage" element={<UsageAnalyticsPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="reports" element={<EvalReportsPage />} />
        <Route path="datasets" element={<GoldenDatasetsV2Page />} />
        <Route path="candidates" element={<GoldenCandidatesPage />} />
        <Route path="evaluators" element={<EvalEvaluatorsPage />} />
        <Route path="settings" element={<EvalPlaceholderPage page="settings" />} />
      </Route>

      {/* ChatShell hosts the persistent ChatPanel for BOTH /ivt/* and the main
          app. Any route inside this Route stays under the same ChatShell fiber,
          so the chat session, streaming, and scope bindings survive when the
          user navigates between IVT and the main app. */}
      <Route element={<ChatShell />}>
        {/* IVT Hub — independent product surface with its OWN access gate:
            IvtLayout enforces IVT_ACCESS_GROUPS ('ivthub' / 'ivthub-editor' /
            'ivthub-admin', any one admits) internally, NOT rocky-user/rocky-admin.
            This route intentionally sits OUTSIDE <AccessGate>; wrapping it in
            AccessGate would lock out legitimate IVT-only users. Do NOT wrap it. */}
        <Route path="/ivt" element={<IvtLayout />}>
          <Route path="solution-map" element={<SolutionMapPage />} />
          <Route path="impact" element={<NewUseCasesPage />} />
          {/* Legacy /ivt/use-cases page retired — send bookmarks/deep-links to
              the redesigned Impact page. The nested :solutionMapId route below
              (UC detail) is unrelated and still active. */}
          <Route path="use-cases" element={<Navigate to="/ivt/impact" replace />} />
          <Route path="use-cases/:solutionMapId" element={<UseCaseDetailPage />} />
          <Route path="assets" element={<AssetsOverviewPage />} />
          <Route path="assets/:assetId" element={<AssetDetailPage />} />
          <Route path="storylines/:storylineId" element={<StorylineDetailPage />} />
          {/* New Assets deprecated (M4): the Assets tab replaced it. Redirect the old
              URL to the v2 Overview so bookmarks/deep-links still land. The
              NewAssetsPage component is kept for now (v2 still reuses NewAssetCreateModal
              + some CSS); its full removal is a separate cleanup CR. */}
          <Route path="new-assets" element={<Navigate to="/ivt/assets" replace />} />
          {/* Leader Home (GM view, DAT-8997). Carried no nav tab while the page was
              being built part by part — reachable only by typing the URL, so existing
              users could not click into a half-built page. All four display parts have
              shipped, so it is now IvtLayout's first NAV_ITEM. */}
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="motions" element={<MotionsPage />} />
          <Route path="motions/:motionId" element={<MotionDetailPage />} />
          <Route path="motion/wave-creation" element={<WaveCreationPage />} />
          {/* Admin surface. No route-level gate here on purpose: IvtLayout already
              admits only IVT members, the nav entry is hidden for non-admins, and the
              service gates BOTH operations on ivthub-admin — including the read. A
              third client-side check would be a fourth place to keep in step. */}
          <Route path="admin/configuration" element={<ConfigurationPage />} />
          {/* Asset Radar admin page (R16). Entry is the Dashboard's radar section
              gear button, not the top nav — the top-level Admin tab stays scoped to
              Configuration. The service gates reads to any `ivthub` member and
              writes to editor/admin + grant (topic-model §3.3); the page's
              Confirm fans out one op per changed row, same as the Configuration
              page's commit. */}
          <Route path="radar-config" element={<RadarConfigPage />} />
          {/* DAT-9259 item 5: role-based landing. See IvtLanding above. */}
          <Route index element={<IvtLanding />} />
        </Route>

        {/* Event Hub — independent product surface with its OWN access gate:
            EventHubLayout enforces membership in any Event Hub group internally
            (see eventHub/utils/authorization), NOT rocky-user/rocky-admin. Like /ivt and /eval this route sits
            OUTSIDE <AccessGate>; wrapping it would lock out legitimate
            event-hub-only users. Do NOT wrap it. */}
        <Route path="/event" element={<EventHubLayout />}>
          {/* Landing surface — two tabs, each a real route so it is linkable and the
              Lifecycle Data half can be built independently. */}
          <Route index element={<EventHubHomePage />} />
          <Route path="lifecycle-data" element={<LifecycleDataPage />} />

          <Route path="events/:eventId" element={<EventDetailPage />} />
          <Route path="events/:eventId/edit" element={<EditEventPage />} />

          {/* One page for both audiences: a marketer reviewing everything and a BD
              looking at their own submissions differ by filter, not by page. */}
          <Route path="nominations" element={<NominationListPage />} />
          {/* Flat, not nested under the event, because the service's route is flat too
              — POST /nominations carries eventId in the body, there is no
              /events/{id}/nominations resource. The event still comes from the URL
              (?eventId=), never from a picker inside the form. */}
          <Route path="nominations/new" element={<CreateNominationPage />} />
          {/* UpdateNomination is PATCH /nominations/{id}: the nominating BD edits, and the
              nomination goes back to review. Same form as create, in edit mode. */}
          <Route path="nominations/:nominationId/edit" element={<EditNominationPage />} />
        </Route>

        {/* Main app — requires rocky-user / rocky-admin, or rocky-marketing-agent-user
            (chat pinned to the Marketing Agent by AgentContext), enforced by <AccessGate>.
            NOTE: /eval and /ivt are deliberately NOT under this gate. Each module
            has its OWN access gate on a DIFFERENT LDAP group (EvalLayout →
            EVAL_PAGE_LDAP_GROUP, IvtLayout → IVT_ACCESS_GROUPS). AccessGate only admits
            the main-app groups (ALLOWED_GROUPS), so wrapping those routes here would deny their
            real audiences. All three are client-side render gates (UX / defense in
            depth); the authoritative check is server-side. */}
        <Route
          path="*"
          element={
            <CloudscapeI18nBoundary>
              <AccessGate>
                <MainAppContent />
              </AccessGate>
            </CloudscapeI18nBoundary>
          }
        />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthContextProvider>
        {/* Providers hoisted above <Routes> so they are a single instance for
            the whole app. Previously each of /ivt/* and /* wrapped its own copy,
            which meant ChatPanel and its context (sessionId, messages, scope)
            were re-initialized on every cross-surface navigation. */}
        <ImpersonationProvider>
          <UserProfileProvider>
            <WorkspaceNavProvider>
              <ReportMetadataProvider>
                <ChatPanelProvider>
                  <AgentProvider>
                    <AppRoutes />
                  </AgentProvider>
                </ChatPanelProvider>
              </ReportMetadataProvider>
            </WorkspaceNavProvider>
          </UserProfileProvider>
        </ImpersonationProvider>
      </AuthContextProvider>
    </BrowserRouter>
  );
}
